import { useSyncExternalStore } from 'react';

/**
 * Layout breakpoints. CSS modules use the same widths in their media queries
 * (phone < 600, tablet 600–1023, desktop ≥ 1024, wide ≥ 1280).
 */
export const MEDIA_QUERIES = {
  phone: '(max-width: 599.98px)',
  compact: '(max-width: 1023.98px)',
  desktop: '(min-width: 1024px)',
  finePointer: '(hover: hover) and (pointer: fine)',
  landscapePhone: '(max-height: 500px) and (orientation: landscape)',
  reducedMotion: '(prefers-reduced-motion: reduce)',
} as const;

export type MediaQueryName = keyof typeof MEDIA_QUERIES;

const subscribeTo = (query: string) => (listener: () => void) => {
  const list = window.matchMedia(query);
  list.addEventListener('change', listener);
  return () => list.removeEventListener('change', listener);
};

const subscriptions = new Map<string, (listener: () => void) => () => void>();

export const matchesMedia = (name: MediaQueryName): boolean => window.matchMedia(MEDIA_QUERIES[name]).matches;

export const useMediaQuery = (name: MediaQueryName): boolean => {
  const query = MEDIA_QUERIES[name];
  let subscribe = subscriptions.get(query);
  if (!subscribe) {
    subscribe = subscribeTo(query);
    subscriptions.set(query, subscribe);
  }
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
};
